import React, { useCallback } from 'react';
import { Button, Icon, Modal } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { useTransactionsContext, ExportFormat } from '../../../Contexts/TransactionsContext';

type TransactionsExportModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filters?: {
    startDate?: string;
    endDate?: string;
  };
  visibleFields?: string[];
}

export const TransactionsExportModal: React.FC<TransactionsExportModalProps> = ({
  open,
  setOpen,
  filters,
  visibleFields
}) => {
  const L = useLocalizedStrings();
  const { initiateTransactionExport, isExporting } = useTransactionsContext();

  const currentlyExporting = isExporting();

  const handleExport = useCallback(async (format: ExportFormat) => {
    // Close the modal immediately
    setOpen(false);

    // Initiate the export - the page will handle toast notifications
    try {
      await initiateTransactionExport({
        format,
        filters,
        visibleFields
      });
    } catch (error) {
      console.error('Failed to initiate export:', error);
    }
  }, [filters, visibleFields, initiateTransactionExport, setOpen]);

  const handleExportCSV = () => handleExport('csv');
  const handleExportXLSX = () => handleExport('xlsx');

  return (
    <Modal title={L.admin_panel.payments.export_recipients} open={open} onClose={() => setOpen(false)}>
      <div className="pb-4">
        {L.admin_panel.payments.export_recipients_desc}
      </div>
      <div className="flex flex-col gap-y-2">
        <Button
          onClick={handleExportCSV}
          variant="secondary"
          className="!justify-start"
          isDisabled={currentlyExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="ArrowDownTrayIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.payments.csv_format}</div>
              <div className="font-light">{L.admin_panel.payments.csv_format_desc}</div>
            </div>
          </div>
        </Button>
        <Button
          onClick={handleExportXLSX}
          variant="secondary"
          className="!justify-start"
          isDisabled={currentlyExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="TableCellsIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.payments.xlsx_format}</div>
              <div className="font-light">{L.admin_panel.payments.xlsx_format_desc}</div>
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  );
}

export default TransactionsExportModal;
